import { LiquidityScenario, TransactionStatus } from '@/types/liquidity-api';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import React, { useState, useEffect, useMemo } from 'react';
import { getAllDatesBetween } from '@/utils/time';
import { formatNumber } from '@/utils/format';
import { filterActiveTransactions } from '@/utils/transactions';

// Enable the dayJs plugins
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);


interface LiquidityBalanceTableProps {
	scenario: LiquidityScenario
}

const LiquidityBalanceTable = ({ scenario }: LiquidityBalanceTableProps) => {
	const dailyData = useMemo(() => {
		const dateFrom = dayjs(scenario.dateFrom);
		const dateTo = dayjs(scenario.dateTo);

		// Only include included/pending transactions transactions in the selected date range
		const includedTransactions = filterActiveTransactions(scenario?.transactions || [], dateFrom, dateTo);

		const allDates = getAllDatesBetween(dateFrom, dateTo);
		const initialState: Record<string, { inflow: number, outflow: number }> = {};
		allDates.forEach(date => {
			initialState[date] = { inflow: 0, outflow: 0 };
		});

		// Group and aggregate by date
		const dailyData = includedTransactions.reduce((acc, transaction) => {
			const date = dayjs(transaction.transactionDate).format('YYYY-MM-DD');
			if (!acc[date]) {
				acc[date] = { inflow: 0, outflow: 0 };
			}
			if (transaction.amount >= 0) {
				acc[date].inflow += transaction.amount;
			} else {
				acc[date].outflow += transaction.amount;
			}
			return acc;
		}, initialState);

		let runningBalance = scenario.openingBalance || 0;
		return Object.entries(dailyData)
			// Sort by date to ensure proper order in the graph
			.sort(([dateA], [dateB]) => (dayjs(dateA).valueOf() - dayjs(dateB).valueOf()))
			.map(([date, data]) => {
				runningBalance += (data.inflow + data.outflow); // outflow is already negative
				return {
					date,
					inflow: data.inflow,
					outflow: data.outflow,
					balance: runningBalance,
				};
			});
	}, [scenario.openingBalance, scenario.transactions, scenario.dateFrom, scenario.dateTo]);

	return (
		<div className="overflow-x-auto whitespace-nowrap p-4">
			<table className="min-w-full">
				<thead>
					<tr className="p-4 border-b">
						<th></th>
						{dailyData.map(entry => (
							<th className="px-4 py-2" key={entry.date}>{entry.date}</th>
						))}
					</tr>
				</thead>
				<tbody>
					<tr className="p-2 border border-dash">
						<td className="p-2 border-r">Inbetalningar</td>
						{dailyData.map(entry => (
							<td className="p-2 text-right border-r" key={entry.date}>{formatNumber(entry.inflow)} kr</td>
						))}
					</tr>
					<tr className="p-2 border-b">
						<td className="p-2 border">Utbetalningar</td>
						{dailyData.map(entry => (
							<td className="p-2 text-right border-r" key={entry.date}>{formatNumber(entry.outflow)} kr</td>
						))}
					</tr>
					<tr className="p-2 border-b">
						<td className="p-2 border">Kassabalans</td>
						{dailyData.map(entry => (
							<td className="p-2 text-right border-r" key={entry.date}>{formatNumber(entry.balance)} kr</td>
						))}
					</tr>
				</tbody>
			</table>
		</div>
	);
};

export default LiquidityBalanceTable;
